import {useEffect, useRef} from 'react';

import type {EagGridStore} from '../store/createEagGridStore';
import type {RowData} from '../types/RowData';

type UseGridSelectionParams = {
  store: EagGridStore;
  rowSelectMode: 'NONE' | 'SINGLE' | 'MULTIPLE';
  selectedRowIds: Set<string>;
  rows: RowData[];
  onRowSelectionChange: ((rows: RowData[]) => void) | undefined;
};

type UseGridSelectionReturn = {
  handleToggleSelection: (rowId: string) => void;
  handleDeselectAll: () => void;
};

/**
 * Manages row selection logic and fires onRowSelectionChange notifications.
 *
 * Selection toggle delegates to the correct store method based on rowSelectMode.
 * The onRowSelectionChange effect fires on all selection paths (select-all,
 * deselect-all, row click, bulk action) — not just direct row clicks.
 */
export const useGridSelection = (params: UseGridSelectionParams): UseGridSelectionReturn => {
  const onRowSelectionChangeRef = useRef(params.onRowSelectionChange);

  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs ref every render
  useEffect(() => {
    onRowSelectionChangeRef.current = params.onRowSelectionChange;
  });

  // Fire onRowSelectionChange with the full selected set whenever selection changes.
  // Using an effect (not inside handleRowClick) ensures it also fires on select-all,
  // deselect-all, and any other selection change paths — not just direct row clicks.
  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive notification; must fire on all selection change paths (select-all, deselect-all, row click, bulk action) — cannot live in a single event handler
  useEffect(() => {
    if (!onRowSelectionChangeRef.current) {
      return;
    }
    const selectedRows = params.rows.filter((row) => params.selectedRowIds.has(row.id));
    onRowSelectionChangeRef.current(selectedRows);
  }, [params.selectedRowIds, params.rows]);

  // Checkbox-initiated toggle — delegates to the same SINGLE/MULTIPLE logic as row click.
  const handleToggleSelection = (rowId: string) => {
    if (params.rowSelectMode === 'MULTIPLE') {
      params.store.toggleRowSelection(rowId);
    } else if (params.rowSelectMode === 'SINGLE') {
      params.store.setSingleSelection(rowId);
    }
  };

  const handleDeselectAll = () => params.store.deselectAll();

  return {
    handleToggleSelection,
    handleDeselectAll,
  };
};
